import { useState, type ReactNode } from 'react';
import { costPerDigitalLeadLabel, digitalLeadsLabel } from '../../../../../lib/portalReport/config';
import type {
  PortalChannelBlock,
  PortalCrmBlock,
  PortalReportLabels,
  PortalSeriesPoint,
} from '../../../../../types/portalReport';
import {
  chartLayout,
  resolveForm,
  shareOfTotal,
  toChartPoints,
  type ChartForm,
  type ChartGrain,
  type ChartKind,
  type ChartMetric,
  type ChartSeries,
  type PlottableMetric,
} from '../../../../../lib/portalReport/chartPlan';
import {
  DELIVERY_METRICS,
  type DeliveryMetricKey,
} from '../../../../../lib/portalReport/delivery';
import {
  CRM_COST_METRICS,
  crmSeriesValues,
  isCrmMetric,
  spendByBucket,
} from '../../../../../lib/portalReport/crm';
import {
  isWebsiteMetric,
  websiteValueAt,
} from '../../../../../lib/ga4/websiteSeries';
import type { PortalWebsitePoint } from '../../../../../types/portalWebsite';
import { chartCardTitle, partMonthNote } from '../../../../../lib/portalReport/chartTitle';
import { ReportTrendChart } from './ReportTrendChart';
import { SwitchGroup } from './controls';
import { metricFormat, oneDecimal } from './format';

// CLIENT PORTAL RESTYLE — the chart CARD: three switches, a legend, a canvas
// and a footnote.
//
// ⚠️⚠️ ONE COMPONENT, FOUR INSTANCES. The report has four chart cards and they
// are this file used four times — not four components that happen to look
// alike. A control that appears on one chart and not the next reads as a bug
// rather than as a decision, so the switches are declared HERE, in one order
// and one position, and the instances differ only in their data and their
// metric vocabulary.
//
// ⚠️ THE FORM OVERRIDE IS REMEMBERED PER METRIC (`touched`), so choosing lines
// for spend does not silently make cost per lead a bar. `resolveForm` owns that
// rule; this component only holds the map.
//
// ⚠️⚠️ THERE IS NO LONGER A MECHANICS FOOTNOTE (2026-09-01). Every card used to
// carry a paragraph explaining how its own chart worked — "One bar per month. A
// month that ran nothing is a real zero; a month nobody measured is not. The
// current month is still running…" — and with the delivery band that became five
// of them on one page. Scott: *"just remove them."*
//
// ⚠️ IT WAS SAFE BECAUSE THE MEANING SURVIVES WHERE THE READER MEETS IT: the
// tooltip says "not measured" in words on a null bucket and "· partial" on an
// unfinished one, and an unmeasured span is shaded and captioned NOT MEASURED on
// the canvas itself. Verified before the sentences were cut — the same check the
// 13-month intro got two rounds ago. The footnote was explaining a gap somewhere
// nobody reads while the explanation already sat on the chart.
//
// ⚠️⚠️ `withheldReason` IS NOT A CHART NOTE AND STAYS. "Last year is not shown:
// reporting for this account starts in Jan 2026" is a fact about the DATA, and
// it is the only thing standing between a client and reading an absent series as
// a collapse. It would be very easy to remove both in one sweep.
//
// ⚠️⚠️ THE **GRAIN** CAN BE LIFTED OUT TO A BAND; THE **FORM** CANNOT
// (2026-09-01, revised 2026-09-02). A band rendering five or six of these cards
// at once would otherwise carry ten or twelve independent controls, which is
// unreadable chrome — so `grain` is an OPTIONAL CONTROLLED prop: pass it and the
// card renders no grain switch and obeys the band; omit it and the card owns its
// own state, exactly as the four original instances do.
//
// ⚠️⚠️ THE FORM DELIBERATELY HAS NO SUCH PROP, and it had one for a day. A band
// switch has to hold ONE value for every card, but `DEFAULT_FORM` opens counts as
// bars and ratios as lines — so the band needed a third "Auto" state meaning
// "each chart in the shape that suits it". That is not a state anyone wants to
// press, and it made a two-option control read as a mode selector for something
// it was not. With the form on the card the per-metric default IS the behaviour,
// and each switch shows what its own chart is doing. Do not re-hoist it; the
// three-state control is what you will end up rebuilding.
//
// ⚠️ A single-metric instance renders NO metric switch either. A segmented
// control with one option is a button that does nothing.

/** The two shapes an instance can be built from. */
export type ChartSource =
  | { kind: 'comparison'; block: PortalChannelBlock }
  | { kind: 'channel'; meta: PortalChannelBlock; google: PortalChannelBlock }
  /**
   * ⚠️ ONE SERIES, NO PRIOR YEAR. The delivery cards deliberately do not carry
   * last year: the band already risks reading as a second results section at
   * five full cards, and a second line on each of the five is exactly the extra
   * weight that would tip it. It plots as a `comparison` chart with a single
   * series, so `chartLayout` gives it `single`/`line` and stacking never
   * arises.
   */
  | { kind: 'delivery'; block: PortalChannelBlock }
  /**
   * ⚠️ A GOAL-SPLIT PANEL's own chart (round 2, 2026-09-25): one series, no
   * prior year, its count carried in `leads` (see `PortalMetaGoalPanel.series`).
   * Same drawing rules as `delivery` — a single comparison series, never
   * stacked — and the same 12 months / reporting period switch as every other
   * chart on the report.
   */
  | {
      kind: 'goal';
      period: readonly PortalSeriesPoint[];
      months: readonly PortalSeriesPoint[];
    }
  /**
   * ⚠️⚠️ THE CRM, WHICH BRINGS ITS OWN SPEND. Two of its six metrics divide AD
   * SPEND by a CRM count, so the ad series travels with the block and the join
   * happens BY BUCKET KEY. Both grains are passed because the card decides which
   * one the grain switch is asking for, and the two must be joined to the
   * MATCHING ad grain — a period-grain CRM series against a monthly ad series
   * would join almost nothing and silently null every cost.
   *
   * ⚠️ ONE SERIES, no prior year: the CRM band already carries six cards, and a
   * second line on each is weight the page cannot take.
   */
  | {
      kind: 'crm';
      block: PortalCrmBlock;
      adPeriod: readonly PortalSeriesPoint[];
      adMonthly: readonly PortalSeriesPoint[];
    }
  /**
   * ⚠️ WEBSITE ANALYTICS (GA4). Its own point shape, read by `websiteValueAt`,
   * which re-derives bounce and average duration per bucket from that bucket's
   * sums. One series, no prior year. A null is NOT MEASURED (no pull covered
   * the day, or the studio had not opened) and draws as a gap, never a zero.
   */
  | {
      kind: 'website';
      period: readonly PortalWebsitePoint[];
      months: readonly PortalWebsitePoint[];
    };

export interface ReportChartCardProps {
  title: string;
  source: ChartSource;
  /** Which metrics this instance offers. ⚠️ `share` is channel-only. */
  metrics: ChartMetric[];
  labels: PortalReportLabels;
  currency: string;
  /** The period's own label, for the grain switch and the legend. */
  periodLabel: string;
  className?: string;
  /**
   * ⚠️ CONTROLLED FROM THE BAND when supplied — the card then renders no grain
   * switch of its own. See the header for why the delivery band does this.
   */
  grain?: ChartGrain;
  /** A heading shown ABOVE the chart — the delivery band's figure. */
  headline?: ReactNode;
  /**
   * ⚠️ Render this INSTEAD of the chart.
   *
   * Distinct from `buildSeries`'s own `withheldReason`, which withholds ONE
   * SERIES (the prior year) while the rest of the chart still draws. This
   * withholds the canvas entirely, for a card that has a figure worth showing
   * and not enough history to draw.
   */
  chartWithheld?: string | null;
  /**
   * ⚠️⚠️ TITLED MODE (2026-11-12, the automotive pages). When set, the card draws a
   * visible title "<section> · <selected metric>" that follows the metric chip,
   * drops the single-series legend (the range toggle already says it), puts every
   * control on ONE row, and footnotes a part-month bar. A BLANK section throws — a
   * card with no title is a wiring bug, not a state. Every other instance omits it
   * and renders exactly as before.
   */
  titleSection?: string;
  /**
   * ⚠️ The count metric is FRACTIONAL (Google's data-driven conversions): the figure
   * and tooltip print one decimal instead of rounding 5.5 to 6. Counts only.
   */
  fractionalCounts?: boolean;
  /** The report's today (YYYY-MM-DD) — the part-month footnote names the day the data runs to. */
  today?: string;
}

/**
 * ⚠️⚠️ A TOTAL RECORD, SO A NEW `ChartMetric` IS A COMPILE ERROR UNTIL IT IS
 * NAMED. This was an if-chain ending in a fallback, and the fallback is exactly
 * how the previous defect happened: every delivery chart announced itself to a
 * screen reader as "Share of each", and nothing on screen showed it. A partial
 * record plus a `??` would reproduce that in a new shape — it is the defect,
 * not the fix.
 *
 * ⚠️ Nothing else would ever catch it: `noUnusedLocals` is off in all three
 * tsconfigs, so an unreached branch raises no warning. Same mechanism
 * `ACCOUNT_FIELD_SURFACES` uses (`Record<keyof Account, …>`) to stop a new
 * Account field going unclassified.
 *
 * ⚠️ Values are FUNCTIONS OF `labels` rather than strings, because leads and
 * cost per lead carry the brand's own vocabulary — a plain record would have
 * forced a fallback back in for exactly those two.
 *
 * ⚠️ The delivery five are read from `DELIVERY_METRICS`, the SAME catalogue the
 * band labels its figures from, so a chart's accessible name cannot disagree
 * with the heading above it.
 */
const METRIC_LABELS: Record<ChartMetric, (labels: PortalReportLabels) => string> = {
  leads: (l) => l.leadsShort,
  spend: () => 'Spend',
  costPerLead: (l) => l.costPerLead,
  share: () => 'Share of each',
  impressions: () => deliveryLabel('impressions'),
  clicks: () => deliveryLabel('clicks'),
  ctr: () => deliveryLabel('ctr'),
  costPerThousand: () => deliveryLabel('costPerThousand'),
  costPerClick: () => deliveryLabel('costPerClick'),
  /**
   * ⚠️⚠️ THE CLIENT'S WORDS, NOT OURS. The first cut of this panel shipped
   * "Leads (CRM)" and it was wrong for exactly the reason this record exists:
   * `(CRM)` is OUR vocabulary carrying the whole distinction between two
   * adjacent numbers on one page.
   *
   * ⚠️⚠️ IT SAID "Enquiries" UNTIL 2026-09-03, on the reasoning that a gym
   * franchisee says enquiries. Scott's clients say TOTAL LEADS, and it now
   * reads the registry rather than a literal — so this card and the hero
   * figure above it cannot come to name the same number two different ways,
   * which was the whole point of the registry.
   *
   * ⚠️ `crmTotalLeads`, NOT `leads`: `leads` is the AD PLATFORMS' conversion
   * count ("Ad Conversions"), a different and much smaller number on the same
   * page. See the field's own note.
   *
   * ⚠️ `crmSales`, `crmCloseRate` and `crmCostPerSale` read the brand's own
   * label registry — that is what it is for, and hardcoding them here would
   * take the choice away from a brand that has made it.
   */
  crmLeads: (l) => l.crmTotalLeads,
  crmDigitalLeads: (l) => digitalLeadsLabel(l),
  crmStaffLeads: () => 'Staff generated leads',
  crmSales: (l) => l.sales,
  crmCloseRate: (l) => l.conversionRate,
  crmCostPerDigitalLead: (l) => costPerDigitalLeadLabel(l),
  crmCostPerSale: (l) => l.costPerSale,
  webSessions: () => 'Sessions',
  webNewUsers: () => 'New users',
  webBounceRate: () => 'Bounce rate',
  webAvgDuration: () => 'Avg session duration',
};

/**
 * ⚠️ THROWS rather than falling back. The catalogue and this record are the same
 * five keys, so a miss means one of them was edited without the other — a bug to
 * surface at the first render, not a raw key quietly read out to a screen-reader
 * user.
 */
function deliveryLabel(key: DeliveryMetricKey): string {
  const found = DELIVERY_METRICS.find((m) => m.key === key);
  if (!found) throw new Error(`DELIVERY_METRICS is missing ${key}`);
  return found.label;
}

/**
 * ⚠️ EXPORTED so the CRM band can head its cards with the SAME string the card
 * announces to a screen reader. It had a second copy of the six labels for one
 * commit; two lists is two chances for a heading and its accessible name to
 * disagree, and the card dedupes them — so a drift would announce the metric
 * twice.
 */
export const METRIC_LABEL = (metric: ChartMetric, labels: PortalReportLabels): string =>
  METRIC_LABELS[metric](labels);

export function ReportChartCard(props: ReportChartCardProps) {
  const first = props.metrics[0] as ChartMetric;
  const [ownGrain, setGrain] = useState<ChartGrain>('months');
  const [metric, setMetric] = useState<ChartMetric>(first);
  const [touched, setTouched] = useState<Partial<Record<ChartMetric, ChartForm>>>({});
  // ⚠️ A controlled prop WINS and suppresses that switch; absent, the card keeps
  // its own state and renders the switch, which is every pre-existing instance.
  const grain = props.grain ?? ownGrain;
  // ⚠️ ALWAYS the card's own — see the header. The default is per metric.
  const form = resolveForm(metric, touched);
  const showGrainSwitch = props.grain === undefined;
  const showMetricSwitch = props.metrics.length > 1;

  const built = buildSeries(props.source, grain, metric, props.labels, props.periodLabel);
  const kind = chartKindOf(props.source);
  const layout = chartLayout(kind, metric, form, built.series.length);


  // ⚠️⚠️ ONE FORMATTER, shared by the axis, the tooltip and — through
  // `deliveryFigureText` — the figure above the chart. The two-branch pair this
  // replaced sent every delivery metric to an integer formatter, so a
  // cost-per-click axis read `0 · 1 · 1 · 2 · 2 · 3` and the tooltip rounded
  // 1.05 to `1` under a headline that said `$1.05`.
  const baseFmt = metricFormat(metric, props.currency);
  const fmt =
    props.fractionalCounts && metric === 'leads' ? { ...baseFmt, value: oneDecimal } : baseFmt;

  // ⚠️ TITLED MODE — see the prop. `chartCardTitle` throws on a blank section.
  const titled = props.titleSection !== undefined;
  const heading = titled
    ? chartCardTitle(props.titleSection as string, METRIC_LABEL(metric, props.labels))
    : null;
  const partNote =
    titled && props.today
      ? partMonthNote(built.series[0]?.points ?? [], grain, props.today)
      : null;
  // ⚠️ A single series needs no key: the range toggle already says what it is.
  const showLegend = !titled || built.series.length > 1 || layout === 'paired' || built.legendNote !== null;

  return (
    <section className={`rpt-card ${props.className ?? ''}`}>
      {props.headline}

      {heading && (
        <h4 className="rpt-chart-title rpt-display" data-chart-title>
          {heading}
        </h4>
      )}

      <div className={`rpt-chart-controls${titled ? ' rpt-chart-controls--row' : ''}`}>
        <div className="rpt-control-group">
          {showGrainSwitch && (
            <SwitchGroup
              ariaLabel="Chart period"
              value={grain}
              onChange={setGrain}
              options={[
                { value: 'months' as const, label: '12 months' },
                { value: 'period' as const, label: 'Reporting period' },
              ]}
            />
          )}
          {showMetricSwitch && (
            <SwitchGroup
              ariaLabel="Metric"
              value={metric}
              onChange={(next) => setMetric(next)}
              options={props.metrics.map((m) => ({
                value: m,
                label: METRIC_LABEL(m, props.labels),
              }))}
            />
          )}
        </div>
        <div className="rpt-control-group">
          {showLegend && (
          <Legend
            series={built.series}
            /* ⚠️ THE SPD/LDS KEY IS NOT A CHART NOTE, and it moved here rather
               than going with the footnotes. "SPD" and "LDS" are two
               abbreviations printed on the chart; without a key they are
               meaningless, which is a different thing from explaining how a bar
               works. It is a legend, so it lives in the legend. */
            note={
              layout === 'paired'
                ? 'SPD is the spend split; LDS is the leads split.'
                : built.legendNote
            }
          />
          )}
          <SwitchGroup
            ariaLabel="Chart form"
            value={form}
            onChange={(next) => setTouched((t) => ({ ...t, [metric]: next }))}
            options={[
              { value: 'bar' as const, label: 'Bar' },
              { value: 'line' as const, label: 'Line' },
            ]}
          />
        </div>
      </div>

      {/* ⚠️ The prior year is WITHHELD, not gapped, when it is not measured
          throughout — and the reason is stated where the missing line would be,
          not tucked into a footnote. */}
      {built.withheldReason && (
        <p className="rpt-note rpt-muted" style={{ marginBottom: 12 }}>
          {built.withheldReason}
        </p>
      )}

      {props.chartWithheld ? (
        /* ⚠️ THE REASON GOES WHERE THE CHART WOULD BE. A card with a figure at
           the top and a blank space beneath it reads as broken — which is
           exactly why this state exists and a missing sparkline needed none. */
        <p className="rpt-note rpt-muted">{props.chartWithheld}</p>
      ) : (
      <ReportTrendChart
        kind={kind}
        metric={metric}
        form={form}
        series={built.series}
        formatAxis={fmt.axis}
        formatValue={fmt.value}
        average={built.average}
        ariaLabel={[
          props.title,
          // ⚠️ Not repeated on a SINGLE-metric card, where the title already IS
          // the metric — "Impressions, Impressions, by month" is what a screen
          // reader would otherwise announce.
          ...(METRIC_LABEL(metric, props.labels) === props.title
            ? []
            : [METRIC_LABEL(metric, props.labels)]),
          grain === 'months' ? 'by month' : 'by day',
        ].join(', ')}
      />
      )}

      {partNote && (
        <p className="rpt-note rpt-muted" data-part-month-note>
          {partNote}
        </p>
      )}
    </section>
  );
}

function Legend({ series, note }: { series: ChartSeries[]; note: string | null }) {
  return (
    <div className="rpt-legend rpt-muted">
      {series.map((s) => (
        <span key={s.label}>
          <span
            className="rpt-swatch"
            style={{
              background:
                s.role === 'a'
                  ? 'var(--rpt-chan-a)'
                  : s.role === 'b'
                    ? 'var(--rpt-chan-b)'
                    : 'var(--rpt-baseline)',
            }}
          />
          {s.label}
        </span>
      ))}
      {note && <span style={{ opacity: 0.75 }}>{note}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Series assembly
// ---------------------------------------------------------------------------

interface BuiltSeries {
  series: ChartSeries[];
  legendNote: string | null;
  withheldReason: string | null;
  average: { value: number; label: string } | null;
}

/**
 * Turn the wire shapes into the chart's own point runs.
 *
 * ⚠️ NOTHING IS COMPUTED HERE THAT THE SERVER ALREADY DECIDED. Coverage states,
 * null handling and the per-bucket CPL all arrive as they are; this only picks
 * which run of points a switch is asking for.
 */
/**
 * ⚠️ `delivery` and `crm` are comparison charts with one series — see the source
 * types. Nothing here ever stacks, because stacking is licensed by
 * `kind === 'channel'` and an additive metric.
 */
function chartKindOf(source: ChartSource): ChartKind {
  return source.kind === 'channel' ? 'channel' : 'comparison';
}

function buildSeries(
  source: ChartSource,
  grain: ChartGrain,
  metric: ChartMetric,
  labels: PortalReportLabels,
  periodLabel: string,
): BuiltSeries {
  if (source.kind === 'crm') {
    // ⚠️ A wiring bug, not a data state: the CRM band only ever offers CRM
    // metrics. Throwing beats returning an empty series, which would draw a
    // blank chart under a real figure and read as missing data.
    if (!isCrmMetric(metric)) {
      throw new Error(`A CRM chart cannot plot ${metric}`);
    }
    const points =
      grain === 'period' ? source.block.periodSeries : source.block.monthlySeries;
    // ⚠️ THE MATCHING AD GRAIN. Joining a period-grain CRM series to a monthly
    // ad series would match almost no keys and null every cost, which looks
    // exactly like "we have no spend data" rather than like a bug.
    const spend = spendByBucket(grain === 'period' ? source.adPeriod : source.adMonthly);
    const values = crmSeriesValues(points, metric, spend);
    const costMetric = CRM_COST_METRICS.has(metric);
    return {
      series: [
        {
          label: grain === 'period' ? periodLabel : 'Last 12 months',
          role: 'a',
          points: points.map((p, i) => ({
            bucket: p.bucket,
            label: p.label,
            value: values[i] ?? null,
            ...(p.partialTo ? { partialTo: p.partialTo } : {}),
            // ⚠️⚠️ A COST BUCKET WITH NO MEASURED AD SPEND IS `no-data` ON THIS
            // CHART, even though the CRM measured the month perfectly well.
            //
            // The renderer CONNECTS ACROSS a null inside a segment, deliberately:
            // a measured bucket with spend and no leads is a KNOWN state, and
            // breaking there looks like a fault to a reader who can see data
            // either side. That rule is right for its case and WRONG for this
            // one — here the null means "we do not know the price", and
            // connecting across it draws a confident line through a month we
            // know nothing about. So the coverage says so, the line breaks, and
            // the canvas shades it NOT MEASURED.
            //
            // ⚠️ A ZERO DENOMINATOR IS NOT THIS. A month with measured spend and
            // no sales has a genuinely undefined cost per sale — a known state —
            // so it keeps `complete` and the line connects across it, exactly as
            // cost per lead does. Only the SPEND side being unmeasured
            // disqualifies the bucket.
            coverage:
              costMetric && p.coverage !== 'no-data' && (spend.get(p.bucket) ?? null) === null
                ? 'no-data'
                : p.coverage,
          })),
        },
      ],
      legendNote: null,
      withheldReason: null,
      average: null,
    };
  }

  if (source.kind === 'website') {
    if (!isWebsiteMetric(metric)) {
      throw new Error(`A website chart cannot plot ${metric}`);
    }
    const points = grain === 'period' ? source.period : source.months;
    return {
      series: [
        {
          label: grain === 'period' ? periodLabel : 'Last 12 months',
          role: 'a',
          points: points.map((p) => ({
            bucket: p.bucket,
            label: p.label,
            value: websiteValueAt(p, metric),
            coverage: p.coverage,
          })),
        },
      ],
      legendNote: null,
      withheldReason: null,
      average: null,
    };
  }
  if (isWebsiteMetric(metric)) {
    throw new Error(`${metric} is a website metric and needs a website source`);
  }

  // ⚠️ Past this line the metric is read off a `PortalSeriesPoint`, which a CRM
  // metric cannot be. The guard is the compiler's, not a comment: `valueAt`
  // takes `PlottableMetric`, which excludes them by type.
  if (isCrmMetric(metric)) {
    throw new Error(`${metric} is a CRM metric and needs a CRM source`);
  }
  const measure: PlottableMetric = metric === 'share' ? 'leads' : metric;

  if (source.kind === 'delivery' || source.kind === 'goal') {
    const points =
      source.kind === 'goal'
        ? grain === 'period'
          ? source.period
          : source.months
        : grain === 'period'
          ? source.block.periodSeries
          : source.block.yearOnYear.current;
    return {
      series: [
        {
          label: grain === 'period' ? periodLabel : 'Last 12 months',
          role: 'a',
          points: toChartPoints(points, measure),
        },
      ],
      legendNote: null,
      withheldReason: null,
      average: null,
    };
  }

  if (source.kind === 'comparison') {
    const { block } = source;
    if (grain === 'period') {
      const points = toChartPoints(block.periodSeries, measure);
      return {
        series: [{ label: 'This period', role: 'a', points }],
        legendNote: null,
        withheldReason: null,
        // ⚠️ A dashed period-average reference line, and ONLY on the cost view —
        // an average impressions bar means nothing useful.
        average: metric === 'costPerLead' ? periodAverage(block.periodSeries, labels) : null,
      };
    }
    const yoy = block.yearOnYear;
    const series: ChartSeries[] = [
      { label: yoy.currentLabel, role: 'a', points: toChartPoints(yoy.current, measure) },
    ];
    if (yoy.previous.length > 0) {
      // ⚠️ Grey and dashed, NOT channel B. It is a BASELINE rather than a third
      // category, and `marine` means "Google" everywhere else on this report —
      // spending it on last year would make the two charts contradict.
      series.push({
        label: yoy.previousLabel,
        role: 'baseline',
        points: toChartPoints(yoy.previous, measure),
      });
    }
    return {
      series,
      legendNote: null,
      withheldReason: yoy.previousUnavailableReason,
      average: null,
    };
  }

  const pick = (block: PortalChannelBlock): PortalSeriesPoint[] =>
    grain === 'period' ? block.periodSeries : block.yearOnYear.current;
  const metaPoints = pick(source.meta);
  const googlePoints = pick(source.google);

  if (metric === 'share') {
    // ⚠️ The ONE view where two different measures appear together, and only
    // because both are then percentages of their OWN total.
    const spendShare = metaPoints.map((p, i) => ({
      bucket: p.bucket,
      label: p.label,
      coverage: p.coverage,
      value: shareOfTotal(p.spend, googlePoints[i]?.spend ?? null),
    }));
    const leadShare = metaPoints.map((p, i) => ({
      bucket: p.bucket,
      label: p.label,
      coverage: p.coverage,
      value: shareOfTotal(p.leads, googlePoints[i]?.leads ?? null),
    }));
    return {
      series: [
        { label: 'Meta share of spend', role: 'a', points: spendShare },
        { label: `Meta share of ${labels.leads.toLowerCase()}`, role: 'b', points: leadShare },
      ],
      legendNote: null,
      withheldReason: null,
      average: null,
    };
  }

  return {
    series: [
      { label: 'Meta', role: 'a', points: toChartPoints(metaPoints, measure) },
      { label: 'Google', role: 'b', points: toChartPoints(googlePoints, measure) },
    ],
    legendNote: null,
    withheldReason: null,
    average: null,
  };
}

/**
 * The period's own cost per lead.
 *
 * ⚠️ RE-DERIVED from the period's summed spend over its summed leads, never the
 * mean of the daily CPLs — averaging ratios answers a different question and
 * gets the wrong number whenever daily volume varies, which is always.
 */
function periodAverage(
  points: readonly PortalSeriesPoint[],
  labels: PortalReportLabels,
): { value: number; label: string } | null {
  let spend = 0;
  let leads = 0;
  for (const p of points) {
    if (p.coverage === 'no-data') continue;
    spend += p.spend ?? 0;
    leads += p.leads ?? 0;
  }
  if (leads <= 0) return null;
  const value = spend / leads;
  return { value, label: `PERIOD ${labels.costPerLead.toUpperCase()}` };
}
